import type { Root } from 'hast';

/**
 * Aliases mapping common fence labels onto Shiki grammar ids.
 * Anything not listed here is passed straight through.
 */
const ALIASES: Record<string, string> = {
  cs: 'csharp',
  'c#': 'csharp',
  dotnet: 'csharp',
  js: 'javascript',
  jsx: 'jsx',
  ts: 'typescript',
  tsx: 'tsx',
  py: 'python',
  rb: 'ruby',
  rs: 'rust',
  golang: 'go',
  kt: 'kotlin',
  sh: 'bash',
  shell: 'bash',
  zsh: 'bash',
  console: 'bash',
  ps: 'powershell',
  ps1: 'powershell',
  pwsh: 'powershell',
  yml: 'yaml',
  md: 'markdown',
  mdx: 'mdx',
  dockerfile: 'docker',
  containerfile: 'docker',
  tf: 'terraform',
  hcl: 'terraform',
  bicep: 'bicep',
  arm: 'json',
  armtemplate: 'json',
  jsonc: 'jsonc',
  json5: 'json5',
  proto: 'proto',
  protobuf: 'proto',
  gql: 'graphql',
  'c++': 'cpp',
  cplusplus: 'cpp',
  htm: 'html',
  plaintext: 'text',
  plain: 'text',
  txt: 'text',
  '': 'text',
};

/** Grammars kept warm because they dominate technical documentation. */
export const COMMON_LANGUAGES = [
  'bash', 'bicep', 'c', 'cpp', 'csharp', 'css', 'diff', 'docker', 'go', 'graphql', 'html',
  'ini', 'java', 'javascript', 'json', 'jsonc', 'kotlin', 'markdown', 'php', 'powershell',
  'python', 'ruby', 'rust', 'sql', 'swift', 'terraform', 'toml', 'tsx', 'typescript', 'xml', 'yaml',
];

const THEMES = { light: 'github-light', dark: 'github-dark' } as const;

export const normalizeLanguage = (language?: string | null): string => {
  const raw = (language ?? '').trim().toLowerCase();
  const [base] = raw.split(/[\s:{]/, 1);
  return ALIASES[base] ?? base ?? 'text';
};

type ShikiModule = typeof import('shiki');
type Highlighter = Awaited<ReturnType<ShikiModule['createHighlighter']>>;

let shikiModule: ShikiModule | null = null;
let highlighterPromise: Promise<Highlighter> | null = null;
const loadedLanguages = new Set<string>(['text']);

const getShiki = async (): Promise<ShikiModule> => {
  shikiModule ??= await import('shiki');
  return shikiModule;
};

const getHighlighter = async (): Promise<Highlighter> => {
  if (!highlighterPromise) {
    highlighterPromise = getShiki().then((shiki) =>
      shiki.createHighlighter({ themes: [THEMES.light, THEMES.dark], langs: [] }),
    );
  }
  return highlighterPromise;
};

/** True when Shiki ships a grammar for the (already normalized) language. */
export const isSupportedLanguage = async (language: string): Promise<boolean> => {
  if (language === 'text') return true;
  const shiki = await getShiki();
  return Object.prototype.hasOwnProperty.call(shiki.bundledLanguages, language);
};

/**
 * Highlights `code` and returns a HAST tree.
 *
 * Dual themes are baked in as `--shiki-light` / `--shiki-dark` CSS variables so
 * switching colour scheme never re-runs the tokenizer.
 *
 * Returns `null` when highlighting is unavailable or fails, letting callers fall
 * back to an unhighlighted block rather than losing the code.
 */
export const highlightToHast = async (
  code: string,
  language?: string | null,
): Promise<Root | null> => {
  try {
    const lang = normalizeLanguage(language);
    const resolved = (await isSupportedLanguage(lang)) ? lang : 'text';

    const highlighter = await getHighlighter();

    if (!loadedLanguages.has(resolved)) {
      await highlighter.loadLanguage(resolved as never);
      loadedLanguages.add(resolved);
    }

    return highlighter.codeToHast(code, {
      lang: resolved,
      themes: THEMES,
      defaultColor: false,
      cssVariablePrefix: '--shiki-',
    }) as Root;
  } catch (error) {
    console.warn('Syntax highlighting failed; rendering plain code.', error);
    return null;
  }
};

/** Test seam: drops cached Shiki state. */
export const resetHighlighter = (): void => {
  shikiModule = null;
  highlighterPromise = null;
  loadedLanguages.clear();
  loadedLanguages.add('text');
};
